import type {
  BracketMatch,
  HistoryResponse,
  IncidentsFile,
  LeagueBundle,
  ScoresResponse,
  TeamRecord,
} from '../../shared/types';
import { seasonIncidentLeader } from './incidents';
import { flattenHistory, seasonTallies, type Appearance, type SeasonTally } from './records';
import { managerDirectory, type ManagerRef } from './rivalries';

/**
 * Career profiles, one per manager who has ever held a roster.
 *
 * Keyed on **user_id** throughout, for the reason `records.ts` spells out:
 * Sleeper renumbers rosters every August, and a roster-keyed career would
 * silently merge two managers' histories the first year they shift.
 *
 * The one thing this file deliberately does NOT report is a finishing position
 * outside the podium. Places 4th and below come out of the losers bracket, and
 * this league runs a TOILET BOWL there -- measured across all four of 2025's
 * consolation games, Sleeper's `w` field names the team that scored FEWER
 * points, because the prize is draft position. An "average finish" column would
 * therefore be built on numbers that are probably inverted, and no arithmetic
 * check can see it: reversing a pair of placements still yields ten distinct
 * seats. Everything here comes from either the winners bracket (whose `w` is
 * verified correct) or the regular-season records, which no bracket touches.
 */

// --- One league year, from whichever source has it --------------------------

export interface SeasonView {
  season: string;
  leagueId: string;
  /** roster_id -> user_id, in THIS season's numbering. */
  owners: Map<number, string>;
  /** user_id -> the team name that manager carried that season. */
  teamNames: Map<string, string>;
  /** user_id -> regular-season record. Sleeper's `fpts` excludes the playoffs. */
  records: Map<string, TeamRecord>;
  bracket: BracketMatch[];
}

/**
 * Every league year the site can see, newest first.
 *
 * `/api/history` covers COMPLETED seasons only and never includes the league in
 * progress, so the current season is prepended off the bundle. Deduped on
 * league id rather than trusted to stay disjoint: counting one season twice
 * would double a career record without producing anything that looks wrong.
 */
export function seasonViews(bundle: LeagueBundle, history: HistoryResponse | null): SeasonView[] {
  const views: SeasonView[] = [];
  const seen = new Set<string>();

  const push = (v: SeasonView) => {
    if (seen.has(v.leagueId)) return;
    seen.add(v.leagueId);
    views.push(v);
  };

  const current: SeasonView = {
    season: bundle.league.season,
    leagueId: bundle.league.league_id,
    owners: new Map(),
    teamNames: new Map(),
    records: new Map(),
    bracket: bundle.bracket ?? [],
  };
  const managers = new Map(bundle.managers.map((m) => [m.user_id, m]));
  for (const t of bundle.teams) {
    if (!t.owner_id) continue;
    const m = managers.get(t.owner_id);
    current.owners.set(t.roster_id, t.owner_id);
    current.records.set(t.owner_id, t.record);
    current.teamNames.set(t.owner_id, m?.team_name || m?.display_name || `Roster ${t.roster_id}`);
  }
  push(current);

  for (const s of history?.seasons ?? []) {
    const v: SeasonView = {
      season: s.season,
      leagueId: s.league_id,
      owners: new Map(),
      teamNames: new Map(),
      records: new Map(),
      bracket: s.bracket,
    };
    // `placements` is empty until a bracket exists; `regularSeason` always holds
    // every roster and carries the same records and the same era-correct names.
    for (const p of s.regularSeason) {
      if (!p.owner_id) continue;
      v.owners.set(p.roster_id, p.owner_id);
      v.records.set(p.owner_id, p.record);
      v.teamNames.set(p.owner_id, p.team_name ?? p.display_name);
    }
    push(v);
  }

  return views.sort((a, b) => Number(b.season) - Number(a.season));
}

// --- Honours ----------------------------------------------------------------

export type HonourKind =
  | 'champion'
  | 'runner-up'
  | 'third'
  | 'high-score'
  | 'efficiency'
  | 'worst-record'
  | 'low-score'
  | 'off-field';

/**
 * A badge carries the CLAIM and no number, deliberately.
 *
 * Every figure a badge could print is already on a tile a few pixels below it,
 * and the card would then assert the same fact twice under two labels -- the
 * headline-and-subtitle defect this repo has now shipped three times (the
 * record book's `11-5`, the trade ledger's player card, and the "best regular
 * season" tile fixed one commit ago). The wooden spoon used to read
 * "Worst record 2-12" directly above a Regular season tile reading "2-12",
 * which is the same defect wearing a badge.
 *
 * It also keeps the row compact, and that now matters: seven kinds exist and a
 * manager can hold several in one season.
 */
export interface Honour {
  season: string;
  kind: HonourKind;
}

export interface SeasonHonours {
  season: string;
  champion: string | null;
  runnerUp: string | null;
  third: string | null;
  worstRecord: string | null;
  worstRecordText: string | null;
  /**
   * Managers who reached the playoff bracket, and EMPTY until the season is
   * decided -- see `seasonHonours` for why that is not merely cautious.
   */
  playoffField: Set<string>;
  /**
   * The league's highest and lowest single-game scores that season, any week,
   * playoffs and consolation included. Null until the season is decided, like
   * everything else here.
   */
  highScore: Appearance | null;
  lowScore: Appearance | null;
  /**
   * Highest share of an optimal lineup actually started, REGULAR SEASON ONLY.
   *
   * That scope is not a choice: it comes off `fpts` / `ppts` on the roster,
   * both of which exclude the playoff weeks with nothing in their names saying
   * so. It is the same figure the record book's efficiency board ranks, so the
   * two pages cannot disagree -- but it means the badge and the podium beside
   * it measure different spans of the same season, which is why the legend
   * entry has to name the regular season on its face.
   */
  mostEfficient: string | null;
  /**
   * Most players with an off-field absence -- a suspension, a holdout or a
   * personal one -- that season.
   *
   * Read off the committed incident ledger rather than from anything in the
   * league's own data, because Sleeper keeps no suspension history and this is
   * the only record of one that exists. Null on a TIE as well as on an empty
   * ledger: unlike every other badge here this ranks small integers, where two
   * managers on the same number is the ordinary case, and a badge prints no
   * figure to show it was a coin flip. `seasonIncidentLeader` carries the
   * argument.
   */
  offField: string | null;
  /** True once the final has been played. */
  decided: boolean;
}

const winShare = (r: TeamRecord) => r.wins + r.ties * 0.5;

export function recordText(wins: number, losses: number, ties: number): string {
  return ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`;
}

const HONOUR_ORDER: HonourKind[] = [
  'champion',
  'runner-up',
  'third',
  'high-score',
  'efficiency',
  'worst-record',
  'low-score',
  'off-field',
];

const honourRank = (kind: HonourKind): number => HONOUR_ORDER.indexOf(kind);

/**
 * The three that count toward `podiums`, named explicitly rather than tested as
 * "not the wooden spoon". That negation was correct while four kinds existed
 * and silently wrong the moment a fifth arrived: the high-score badge would
 * have counted as a podium finish and reordered the whole page.
 */
const PODIUM = new Set<HonourKind>(['champion', 'runner-up', 'third']);

/**
 * The podium, read off the WINNERS bracket's placement games.
 *
 * `p:1` sends its winner to 1st and its loser to 2nd; `p:3` decides third. Both
 * are verified against 2025 (dre7777 over danozpro, thetale third) and neither
 * touches the losers bracket, so the toilet-bowl inversion cannot reach them.
 * Each is null while its match is pending, which is what keeps a mid-December
 * page from awarding a title nobody has won yet.
 *
 * The wooden spoon hangs off the same `decided` gate even though the regular
 * season settles it a fortnight earlier. One rule for the whole row is worth
 * more than two weeks of earliness: it means a season's badges appear together
 * or not at all, and there is no window in which the page has opinions about
 * the bottom of the table but none about the top.
 *
 * **`playoffField` is gated on it too, and that one is load-bearing rather than
 * tidy.** Sleeper PRE-SEEDS the bracket months early: measured on 2026 in
 * August, before a single game, `/winners_bracket` already returned seven
 * matches with real roster ids in round one (6 v 8, 10 v 3) and nulls
 * everywhere else. Read without the gate, that is a six-team playoff field
 * invented from a schedule -- six managers credited with qualifying for a
 * season whose week 1 has not happened. Nothing about the payload says it is
 * provisional; only the absent results do.
 */
export function seasonHonours(
  view: SeasonView,
  games: Appearance[],
  incidents: IncidentsFile | null = null,
): SeasonHonours {
  const user = (rosterId: number | null) =>
    rosterId == null ? null : (view.owners.get(rosterId) ?? null);

  const winners = view.bracket.filter((m) => m.bracket === 'winners');
  const final = winners.find((m) => m.placement === 1);
  const bronze = winners.find((m) => m.placement === 3);

  const champion = user(final?.winner ?? null);

  const playoffField = new Set<string>();
  if (champion) {
    for (const m of winners) {
      for (const id of [m.team1, m.team2]) {
        const u = user(id);
        if (u) playoffField.add(u);
      }
    }
  }

  // Taken from the regular-season table, never from the bracket -- see the note
  // at the top of this file. Lowest win share, then fewest points, which is the
  // tiebreak Sleeper's own standings use, read backwards.
  let worst: { userId: string; record: TeamRecord } | null = null;
  if (champion) {
    for (const [userId, record] of view.records) {
      if (
        !worst ||
        winShare(record) < winShare(worst.record) ||
        (winShare(record) === winShare(worst.record) && record.pointsFor < worst.record.pointsFor)
      ) {
        worst = { userId, record };
      }
    }
  }

  // Ties are not shared. Scores are hundredths over ~160 manager-weeks a
  // season, so two identical ones are vanishingly unlikely -- but `>` rather
  // than `>=` still makes the tiebreak the EARLIER game rather than whichever
  // one the loop happened to reach last, because `flattenHistory` hands them
  // over in chronological order. Deterministic beats arbitrary even where it
  // should never be reached.
  let highScore: Appearance | null = null;
  let lowScore: Appearance | null = null;
  if (champion) {
    for (const g of games) {
      if (!highScore || g.points > highScore.points) highScore = g;
      if (!lowScore || g.points < lowScore.points) lowScore = g;
    }
  }

  // Lineup efficiency, from the same `fpts` / `ppts` pair the record book's
  // board uses. The `> 0` test is not only defensive: for a season in progress
  // Sleeper reports BOTH as 0.00 on all ten rosters -- measured on 2026 in
  // August -- so an ungated read would award this to whichever roster the map
  // happened to yield first, on a ratio of nothing over nothing. The `decided`
  // gate already stops that; this stops a mid-season roster that has scored
  // while `ppts` lags, which would divide by zero and rank as NaN.
  //
  // A strict `>` again, though here a near-tie is realistic rather than
  // theoretical: 2025's top two sit 0.05 percentage points apart, and a badge
  // prints no number to show how close it was. That is the claim being made --
  // the leader, not the margin -- and the record book's board is where the gap
  // is visible.
  let efficient: { userId: string; ratio: number } | null = null;
  if (champion) {
    for (const [userId, record] of view.records) {
      if (record.pointsFor <= 0 || record.potentialPoints <= 0) continue;
      const ratio = record.pointsFor / record.potentialPoints;
      if (!efficient || ratio > efficient.ratio) efficient = { userId, ratio };
    }
  }

  return {
    season: view.season,
    champion,
    runnerUp: user(final?.loser ?? null),
    third: user(bronze?.winner ?? null),
    highScore,
    lowScore,
    mostEfficient: efficient?.userId ?? null,
    // Gated on `decided` with the rest of the row. The ledger is a running
    // count, so mid-season it names whoever is ahead today rather than a
    // season's leader -- and the file's own rule is that a season's badges
    // appear together or not at all.
    offField: champion ? seasonIncidentLeader(incidents, view.season) : null,
    worstRecord: worst?.userId ?? null,
    worstRecordText: worst
      ? recordText(worst.record.wins, worst.record.losses, worst.record.ties)
      : null,
    playoffField,
    decided: champion !== null,
  };
}

// --- Classifying a game -----------------------------------------------------

/**
 * What a game WAS, as opposed to merely when it was played.
 *
 * `/api/scores` flags a game as `isPlayoff` on the week alone, so in this
 * league's 2025 the semifinal and the toilet bowl carry the identical flag. A
 * career "postseason record" taken off it would credit consolation wins as
 * playoff wins -- jacobfiggins, 2-12 in the regular season, would read 2-1 in
 * the postseason.
 *
 * `unclassified` is a real answer rather than a failure: a playoff-week game
 * with no bracket entry (a season whose playoffs were never seeded) is one we
 * cannot honestly call either. Folding it into `playoff` would be the
 * null-versus-zero mistake this repo keeps meeting -- asserting a fact to avoid
 * an awkward gap.
 */
export type GameKind = 'regular' | 'playoff' | 'consolation' | 'unclassified';

const pairKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

/**
 * season -> roster pair -> the bracket that pair met in.
 *
 * The unordered pair is the join key because a `BracketMatch` carries no week
 * and a `Game` carries no bracket. Verified on 2025: the two brackets hold 11
 * distinct pairs and the three playoff weeks hold exactly those 11 games, no
 * pair twice. That holds structurally rather than by luck -- the two brackets
 * draw from disjoint sets of teams, and single elimination cannot pair the same
 * two teams twice inside one.
 */
export function bracketIndex(views: SeasonView[]): Map<string, Map<string, 'winners' | 'losers'>> {
  const out = new Map<string, Map<string, 'winners' | 'losers'>>();
  for (const v of views) {
    const pairs = new Map<string, 'winners' | 'losers'>();
    for (const m of v.bracket) {
      if (m.team1 == null || m.team2 == null) continue;
      pairs.set(pairKey(m.team1, m.team2), m.bracket);
    }
    out.set(v.season, pairs);
  }
  return out;
}

export function classify(
  a: Appearance,
  index: Map<string, Map<string, 'winners' | 'losers'>>,
): GameKind {
  if (!a.isPlayoff) return 'regular';
  const hit = index.get(a.season)?.get(pairKey(a.rosterId, a.opponentRosterId));
  if (hit === 'winners') return 'playoff';
  if (hit === 'losers') return 'consolation';
  return 'unclassified';
}

// --- Career records ---------------------------------------------------------

export interface CareerRecord {
  wins: number;
  losses: number;
  ties: number;
  games: number;
  pointsFor: number;
  pointsAgainst: number;
}

const emptyCareer = (): CareerRecord => ({
  wins: 0,
  losses: 0,
  ties: 0,
  games: 0,
  pointsFor: 0,
  pointsAgainst: 0,
});

function addGame(c: CareerRecord, a: Appearance): void {
  c.games += 1;
  c.pointsFor = Math.round((c.pointsFor + a.points) * 100) / 100;
  c.pointsAgainst = Math.round((c.pointsAgainst + a.against) * 100) / 100;
  if (a.result === 'W') c.wins += 1;
  else if (a.result === 'L') c.losses += 1;
  else c.ties += 1;
}

/** Null, not zero, for a manager who has not played -- a 0-0 record is not .000. */
export const careerPct = (c: CareerRecord): number | null =>
  c.games === 0 ? null : (c.wins + c.ties * 0.5) / c.games;

export const careerPpg = (c: CareerRecord): number | null =>
  c.games === 0 ? null : c.pointsFor / c.games;

/**
 * The four buckets recombined -- every game a manager has ever played.
 *
 * Deliberately derived rather than accumulated as a fifth bucket alongside
 * them: the harness's load-bearing invariant is that a game lands in exactly
 * ONE bucket, and a parallel total maintained by the same loop would satisfy
 * that check while being free to drift from it. Summing here cannot drift.
 */
export function careerTotals(p: ManagerProfile): CareerRecord {
  const out = emptyCareer();
  for (const c of [p.regular, p.playoff, p.consolation, p.unclassified]) {
    out.games += c.games;
    out.wins += c.wins;
    out.losses += c.losses;
    out.ties += c.ties;
    out.pointsFor = Math.round((out.pointsFor + c.pointsFor) * 100) / 100;
    out.pointsAgainst = Math.round((out.pointsAgainst + c.pointsAgainst) * 100) / 100;
  }
  return out;
}

/**
 * Points for minus points against, across every game.
 *
 * Null rather than 0.00 before a manager's first game, for the reason the whole
 * file keeps meeting: an even differential and no differential at all are
 * different facts, and this is the one metric where the placeholder value is
 * also a perfectly ordinary real one.
 */
export function careerDifferential(p: ManagerProfile): number | null {
  const all = careerTotals(p);
  return all.games === 0 ? null : Math.round((all.pointsFor - all.pointsAgainst) * 100) / 100;
}

/**
 * Career lineup efficiency: points scored over points an optimal lineup would
 * have scored, pooled across seasons.
 *
 * Pooled, NOT the mean of each season's ratio -- a 17-game season and a 3-game
 * one do not carry equal weight, and averaging ratios would give them one.
 *
 * REGULAR SEASON ONLY, because `fpts` and `ppts` are and nothing in their names
 * says so. That makes this tile and the `Regular season` tile agree on span,
 * and makes both disagree with the `Point differential` tile beside them, which
 * counts every game -- hence the note on each naming its own span.
 *
 * Note it need NOT agree with the 🎯 badge on the same card. The badge is one
 * season's league leader; this is a career figure, so once a second season
 * lands the best career efficiency may belong to someone who never led a single
 * year. Both are right, which is exactly why each says what it measures.
 */
export const careerEfficiency = (p: ManagerProfile): number | null =>
  p.lineupPotential > 0 ? p.lineupFor / p.lineupPotential : null;

// --- The profile ------------------------------------------------------------

export interface ManagerProfile {
  user: ManagerRef;
  /** Most recent team name this manager used. */
  teamName: string;
  /** Current-season roster, or null for a manager who has left the league. */
  rosterId: number | null;
  /** Every league year they held a roster, newest first. */
  seasons: string[];
  honours: Honour[];
  titles: number;
  podiums: number;
  /** Seasons whose playoff field they reached, out of `decidedSeasons`. */
  playoffAppearances: number;
  decidedSeasons: number;
  regular: CareerRecord;
  playoff: CareerRecord;
  consolation: CareerRecord;
  unclassified: CareerRecord;
  /**
   * Career lineup efficiency, kept as its two COMPONENTS rather than a ratio.
   *
   * Pooling is only possible from the parts, and the tile prints both ("2,079.54
   * of 2,461.38 possible"), so storing the quotient would throw away the half a
   * reader can actually check. Both are 0 for a manager whose seasons have all
   * scored nothing, which `careerEfficiency` turns into null.
   */
  lineupFor: number;
  lineupPotential: number;
  /** Highest and lowest single-game scores, any round. Null before game one. */
  best: Appearance | null;
  worst: Appearance | null;
  /** Best regular-season record. Null until one of their seasons is four games old. */
  bestSeason: SeasonTally | null;
  /**
   * How many of their seasons are long enough to rank.
   *
   * The view needs it to decide whether `bestSeason` is worth showing at all:
   * with one season on the books it is the career regular-season record by
   * definition, and a card printing the same "2-12" as both a headline and the
   * tile beside it is a defect this repo has shipped twice -- once on the
   * record book, once on the player card in the trade ledger. Not something a
   * numeric fixture can see, since both figures are right.
   */
  scoredSeasons: number;
}

/**
 * A season shorter than this cannot hold the "best season" tile, for the reason
 * `best-record` in the record book drops one: a manager who happens to start
 * 1-0 would otherwise outrank every completed season until autumn.
 */
const MIN_SEASON_GAMES = 4;

export function buildProfiles(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
  scores: ScoresResponse[],
  incidents: IncidentsFile | null = null,
): ManagerProfile[] {
  const views = seasonViews(bundle, history);
  const index = bracketIndex(views);
  const dir = managerDirectory(bundle, history);

  // Playoffs included: this function splits them out itself, and asking
  // `flattenHistory` for the regular season alone would discard every game the
  // other two buckets are made of.
  const { appearances } = flattenHistory(scores, true);

  const bySeason = new Map<string, Appearance[]>();
  for (const a of appearances) {
    const list = bySeason.get(a.season);
    if (list) list.push(a);
    else bySeason.set(a.season, [a]);
  }

  // Explicit arrow, NOT `views.map(seasonHonours)`. `map` passes (value, index,
  // array), so the point-free form would hand the index in as the games list --
  // which typechecks as nothing and silently awards no high or low score. Now
  // that a third parameter exists it would also hand the whole array in as the
  // ledger, so the trap has grown rather than gone away.
  const honours = views.map((v) => seasonHonours(v, bySeason.get(v.season) ?? [], incidents));

  const profiles = new Map<string, ManagerProfile>();
  const ensure = (userId: string): ManagerProfile => {
    const held = profiles.get(userId);
    if (held) return held;
    const ref = dir.get(userId) ?? { user_id: userId, name: 'Unknown', avatar: null, active: false };
    const fresh: ManagerProfile = {
      user: ref,
      teamName: ref.name,
      rosterId: null,
      seasons: [],
      honours: [],
      titles: 0,
      podiums: 0,
      playoffAppearances: 0,
      decidedSeasons: 0,
      regular: emptyCareer(),
      playoff: emptyCareer(),
      consolation: emptyCareer(),
      unclassified: emptyCareer(),
      lineupFor: 0,
      lineupPotential: 0,
      best: null,
      worst: null,
      bestSeason: null,
      scoredSeasons: 0,
    };
    profiles.set(userId, fresh);
    return fresh;
  };

  // Seasons held, and the era-correct team name. `views` is newest first, so
  // the first name a manager is seen under is their current one.
  for (const v of views) {
    for (const [rosterId, userId] of v.owners) {
      const p = ensure(userId);
      if (p.seasons.length === 0) p.teamName = v.teamNames.get(userId) ?? p.user.name;
      p.seasons.push(v.season);
      if (v.leagueId === bundle.league.league_id) p.rosterId = rosterId;
    }

    // Lineup efficiency, off the same `fpts` / `ppts` pair the badge ranks and
    // the record book's board uses. A season contributes only when both are
    // positive: Sleeper reports both as 0.00 for a season in progress, so the
    // guard is what keeps the current league year from diluting nothing into
    // the pool -- and it is why a 2026-preseason card still prints 2025's
    // figure rather than an em-dash.
    for (const [userId, record] of v.records) {
      if (record.pointsFor <= 0 || record.potentialPoints <= 0) continue;
      const p = ensure(userId);
      p.lineupFor = Math.round((p.lineupFor + record.pointsFor) * 100) / 100;
      p.lineupPotential = Math.round((p.lineupPotential + record.potentialPoints) * 100) / 100;
    }
  }

  for (const h of honours) {
    if (!h.decided) continue;
    for (const userId of h.playoffField) ensure(userId).playoffAppearances += 1;
    for (const [userId, kind] of [
      [h.champion, 'champion'],
      [h.runnerUp, 'runner-up'],
      [h.third, 'third'],
      [h.highScore?.userId ?? null, 'high-score'],
      [h.mostEfficient, 'efficiency'],
      [h.worstRecord, 'worst-record'],
      [h.lowScore?.userId ?? null, 'low-score'],
      [h.offField, 'off-field'],
    ] as Array<[string | null, HonourKind]>) {
      if (!userId) continue;
      const p = ensure(userId);
      p.honours.push({ season: h.season, kind });
      if (kind === 'champion') p.titles += 1;
      if (PODIUM.has(kind)) p.podiums += 1;
    }
  }

  const decidedSeasons = new Set(honours.filter((h) => h.decided).map((h) => h.season));
  for (const p of profiles.values()) {
    p.decidedSeasons = p.seasons.filter((s) => decidedSeasons.has(s)).length;
    p.honours.sort(
      (a, b) => Number(b.season) - Number(a.season) || honourRank(a.kind) - honourRank(b.kind),
    );
  }

  const regularOnly: Appearance[] = [];
  for (const a of appearances) {
    const p = ensure(a.userId);
    const kind = classify(a, index);
    if (kind === 'regular') {
      addGame(p.regular, a);
      regularOnly.push(a);
    } else if (kind === 'playoff') addGame(p.playoff, a);
    else if (kind === 'consolation') addGame(p.consolation, a);
    else addGame(p.unclassified, a);

    if (!p.best || a.points > p.best.points) p.best = a;
    if (!p.worst || a.points < p.worst.points) p.worst = a;
  }

  // Best REGULAR-season record, so this tile and the wooden-spoon badge measure
  // the same thing. `seasonTallies` counts whatever it is handed.
  for (const t of seasonTallies(regularOnly)) {
    if (t.games < MIN_SEASON_GAMES) continue;
    const p = profiles.get(t.userId);
    if (!p) continue;
    p.scoredSeasons += 1;
    const share = (x: SeasonTally) => x.wins + x.ties * 0.5;
    const held = p.bestSeason;
    if (!held || share(t) > share(held) || (share(t) === share(held) && t.points > held.points)) {
      p.bestSeason = t;
    }
  }

  return [...profiles.values()].sort(
    (a, b) =>
      b.titles - a.titles ||
      b.podiums - a.podiums ||
      (careerPct(b.regular) ?? 0) - (careerPct(a.regular) ?? 0) ||
      b.regular.pointsFor - a.regular.pointsFor,
  );
}
